/**
 * SCADA 3D integrado: rede do cadastro, simulação, telemetria ao vivo e agente do setor — Nexora Grid (US-405).
 *
 * Criado em: 17/10/2026 · Última modificação: 26/10/2026 · Versão: 2.4.0
 *
 * Promove a prova S3 v2 para a aplicação, lendo a API em vez de arquivos locais:
 * - rede: importação vigente (`/api/v1/ativos/importacoes` → `/rede`) e equipamentos do cadastro (`/ativos/geojson`);
 * - cotas pelo relevo (Terrarium); modelo EPANET gerado do cadastro e simulado no navegador (24 h);
 * - telemetria ao vivo (GUS-1104): reservatórios e bombas com dispositivo usam o valor real (selo "ao vivo");
 * - painel do setor com o chat do agente (ponte autenticada do backend → serviço de agentes);
 * - sem manobra: análise. A manobra é só no CCO.
 * v2 (retorno de 17/10/2026): SÓ DADOS AO VIVO (a simulação fica na tela Rede): sem linha do tempo; nível, bombas, painel
 * do setor e chat pela telemetria e pelos alarmes reais; quadros ao lado dos equipamentos com gráfico da última hora;
 * "Entrar na casa" leva a câmera ao quadro de comando da estação. A água nos trechos segue o fluxo estimado da hora.
 * v2.1 (retorno de 18/10/2026): painel de setores recolhível, com filtro e lista em duas colunas que rola dentro do painel
 * (mostra todos); o menu do sistema abre por cima do 3D (o palco é um contexto de empilhamento próprio).
 * v2.4 (US-3602): camada "edificações" (bases abertas: caixas instanciadas pela área e altura, cor por uso) com ficha e
 * a atribuição das fontes; liga e desliga na legenda.
 */
import { OrbitControls } from "@react-three/drei";
import { Canvas, useFrame, useThree } from "@react-three/fiber";
import { Suspense, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import * as THREE from "three";
import { api, ErroApi } from "../api";
import { chave, useTempoReal } from "../cco/useTempoReal";
import { useSessao } from "../sessao";
import type { Tempo } from "./Agua";
import { alturaCena, Cena, corDoSetor } from "./Cena";
import { gerarInp } from "./modelo";
import { PainelSetor } from "./PainelSetor";
import { aplicarCotas, nosPorSetor, redeDaApi, type Equipamento, type FeicaoAtivo, type NoApi, type Rede, type TrechoApi } from "./rede";
import { carregarRelevo, type Relevo } from "./relevo";
import { BlocosQuadro } from "./Quadro3d";
import "./scada3d.css";
import { situacaoSetorAoVivo, type GrupoAlarmeSetor } from "./setor";
import { simular, type Simulacao } from "./simulacao";
import { PainelGemeo, PontosGemeo, useGemeo } from "./GemeoDigital";
import { Edificacoes3d } from "./Edificacoes3d";
import { carregarEdificacoes, NOME_USO, type ColecaoEdificacoes, type FeicaoEdificacao } from "../edificacoes";

const SEGUNDOS_POR_HORA = 3;
const NOMES: Record<string, string> = {
  reservatorio: "Reservatório", bomba: "Bomba", elevatoria: "Estação elevatória", poco_profundo: "Poço profundo (bomba submersa)",
  macromedidor: "Macromedidor", vrp: "VRP", registro: "Registro", registro_fechado: "Registro fechado", ventosa: "Ventosa", hidrante: "Hidrante",
  ponto_pressao: "Ponto de pressão",
};
const horaTexto = (h: number) => `${String(Math.floor(h) % 24).padStart(2, "0")}:${String(Math.floor((h % 1) * 60)).padStart(2, "0")}`;
type Alvo = { ponto: THREE.Vector3; distancia: number; direcao?: THREE.Vector3 };

function Foco({ alvo, aoChegar }: { alvo: Alvo | null; aoChegar: () => void }) {
  const camera = useThree((s) => s.camera);
  const controles = useThree((s) => s.controls) as unknown as { target: THREE.Vector3; update: () => void } | null;
  useFrame(() => {
    if (!alvo || !controles) return;
    const direcao = alvo.direcao ? alvo.direcao.clone().normalize() : camera.position.clone().sub(controles.target).normalize();
    if (!alvo.direcao && direcao.y < 0.35) direcao.setY(0.35).normalize();
    const destino = alvo.ponto.clone().add(direcao.multiplyScalar(alvo.distancia));
    controles.target.lerp(alvo.ponto, 0.1);
    camera.position.lerp(destino, 0.1);
    controles.update();
    if (camera.position.distanceTo(destino) < 1 && controles.target.distanceTo(alvo.ponto) < 0.5) aoChegar();
  });
  return null;
}

/** Hora atual (o fluxo animado usa a simulação da hora corrente; os números são ao vivo). */
function Relogio({ tempo }: { tempo: React.MutableRefObject<Tempo> }) {
  const acumulado = useRef(0);
  useFrame((_, delta) => {
    acumulado.current += delta;
    if (acumulado.current >= 30) { acumulado.current = 0; const d = new Date(); tempo.current.hora = d.getHours() + d.getMinutes() / 60; }
  });
  return null;
}

function Medidor() {
  const quadros = useRef(0);
  const inicio = useRef(performance.now());
  useFrame(() => {
    quadros.current++;
    const agora = performance.now();
    if (agora - inicio.current >= 1000) {
      (window as unknown as { __fps: number }).__fps = (quadros.current * 1000) / (agora - inicio.current);
      quadros.current = 0;
      inicio.current = agora;
    }
  });
  return null;
}

export function Scada3d() {
  const { empresa } = useSessao();
  const [parametros] = useSearchParams();
  const vivo = useTempoReal(empresa);
  const [rede, setRede] = useState<Rede | null>(null);
  const [origens, setOrigens] = useState<{ id: string; rede: string; arquivo: string; municipio: string | null; vigente: boolean }[]>([]);
  const [nomesMunicipios, setNomesMunicipios] = useState<Record<string, string>>({});
  const [origem, setOrigem] = useState("");
  const origemEpanet = Boolean(origem);
  const cenarioSimulado = origens.find((item) => item.id === origem)?.rede === "cenario_simulado";
  const gemeo = useGemeo(origem);
  const [relevo, setRelevo] = useState<Relevo | null>(null);
  const [sim, setSim] = useState<Simulacao | null>(null);
  const [estado, setEstado] = useState("lendo a rede do cadastro…");
  const [erro, setErro] = useState<string | null>(null);
  const [selecionado, setSelecionado] = useState<Equipamento | null>(null);
  const [setorSelecionado, setSetorSelecionado] = useState<string | null>(null);
  const [alvo, setAlvo] = useState<Alvo | null>(null);
  const [destaque, setDestaque] = useState(false);
  const [rotulos, setRotulos] = useState(true);
  const [quadros, setQuadros] = useState(true);
  const [tubulacao, setTubulacao] = useState(true);
  const [mostrarEdificacoes, setMostrarEdificacoes] = useState(true);
  const [edificacoes, setEdificacoes] = useState<ColecaoEdificacoes | null>(null);
  const [edificacao, setEdificacao] = useState<FeicaoEdificacao | null>(null);
  const [dentroDe, setDentroDe] = useState<Equipamento | null>(null);
  const [legendaAberta, setLegendaAberta] = useState(true);
  const [filtroSetor, setFiltroSetor] = useState("");
  const [alarmes, setAlarmes] = useState<GrupoAlarmeSetor[]>([]);
  const tempo = useRef<Tempo>({ hora: new Date().getHours() + new Date().getMinutes() / 60, tocando: false, ritmo: 1 });

  // US-3602: edificações de bases abertas do município (o da rede escolhida ou o primeiro cadastrado)
  useEffect(() => {
    const municipio = origens.find((i) => i.id === origem)?.municipio ?? Object.keys(nomesMunicipios)[0];
    if (!empresa || !municipio) return;
    carregarEdificacoes(empresa, municipio).then(setEdificacoes).catch(() => setEdificacoes(null));
  }, [empresa, origem, origens, nomesMunicipios]);

  useEffect(() => {
    if (!empresa) return;
    let ativo = true;
    setOrigem(""); setOrigens([]); setSelecionado(null); setRede(null); setRelevo(null); setErro(null);
    Promise.all([
      api<typeof origens>(`/v1/ativos/dados-reais?empresa=${empresa}`),
      api<{ municipios: { codigo: string; nome: string; cadastrado: boolean }[] }>(`/v1/municipios?empresa=${empresa}`),
    ]).then(([lista, cadastro]) => {
      if (!ativo) return;
      setOrigens(lista.filter((i) => i.vigente && i.municipio));
      setNomesMunicipios(Object.fromEntries(cadastro.municipios.filter((m) => m.cadastrado).map((m) => [m.codigo, m.nome])));
    }).catch(() => { if (ativo) { setOrigens([]); setNomesMunicipios({}); } });
    return () => { ativo = false; };
  }, [empresa]);

  useEffect(() => {
    if (!empresa) return;
    let ativo = true;
    (async () => {
      try {
        const importacoes = await api<{ id: string; vigente: boolean }[]>(`/v1/ativos/importacoes?empresa=${empresa}`);
        const vigente = importacoes.find((i) => i.vigente);
        // US-2306: sem DXF vigente, abre a primeira rede EPANET (dados reais) associada a um município
        if (!vigente && !origem && origens.length) { setOrigem(origens[0].id); return; }
        const selecionada = origens.find((i) => i.id === origem);
        if (!selecionada && !vigente) throw new Error("Importe a rede da engenharia em Cadastro › Importar.");
        const [redeApi, geo, limites] = selecionada
          ? await Promise.all([api<{ nos: NoApi[]; trechos: TrechoApi[] }>(`/v1/ativos/dados-reais/${selecionada.id}/rede`),
            api<{ features: FeicaoAtivo[] }>(`/v1/ativos/geojson?empresa=${empresa}`), api<Rede["divisas"]>(`/v1/ativos/dados-reais/${selecionada.id}/divisas`)] as const)
          : await Promise.all([api<{ nos: NoApi[]; trechos: TrechoApi[] }>(`/v1/ativos/importacoes/${vigente!.id}/rede`),
            api<{ features: FeicaoAtivo[] }>(`/v1/ativos/geojson?empresa=${empresa}`), Promise.resolve(undefined)] as const);
        if (!ativo) return;
        const feicoes = await Promise.all(geo.features.map(async (f) => {
          if (f.properties.tipo !== "reservatorio" || !f.properties.ativo_id) return f;
          try {
            const ficha = await api<{ ficha: Equipamento["reservatorio"] }>(`/v1/ativos/${f.properties.ativo_id}/ficha`);
            return { ...f, properties: { ...f.properties, reservatorio: ficha.ficha } };
          } catch { return f; }
        }));
        if (!ativo) return;
        const r = redeDaApi(redeApi.nos, redeApi.trechos, feicoes);
        r.divisas = limites;
        setEstado("carregando o relevo…");
        const rel = await carregarRelevo(r);
        if (!ativo) return;
        aplicarCotas(r, rel.cota);
        setRede(r);
        setErro(null);
        setRelevo(rel);
        if (selecionada) {
          setSim(null);
          setEstado(selecionada.rede === "cenario_simulado"
            ? `EPANET simulado · ${r.trechos.length} trechos · telemetria fictícia`
            : `EPANET ${selecionada.rede} · ${r.trechos.length} trechos · setores do modelo · sem simulação hidráulica`);
        } else {
          setEstado("simulando 24 h…");
          const s = await simular(gerarInp(r, rel.cota));
          if (!ativo) return;
          setSim(s);
          setEstado(`${r.trechos.length} trechos · ${r.equipamentos.length} equipamentos · simulação ${s.tempoMs.toFixed(0)} ms`);
        }
      } catch (e) {
        if (ativo) setErro(e instanceof Error ? e.message : String(e));
      }
    })();
    return () => { ativo = false; };
  }, [empresa, origem, origens]);

  const setores = useMemo(() => {
    if (!rede) return [] as { setor: string; trechos: number }[];
    const contagem = new Map<string, number>();
    rede.trechos.forEach((t) => contagem.set(t.setor, (contagem.get(t.setor) ?? 0) + 1));
    return [...contagem.entries()].map(([setor, trechos]) => ({ setor, trechos })).sort((a, b) => b.trechos - a.trechos);
  }, [rede]);
  const nomesSetores = useMemo(() => setores.map((s) => s.setor), [setores]);
  const nosSetor = useMemo(() => (rede ? nosPorSetor(rede) : new Map<string, Set<string>>()), [rede]);

  // valor ao vivo do dispositivo (null = sem telemetria ligada ou sem leitura recente)
  const ler = useCallback((device: string | null | undefined, medida: string) => {
    const leitura = device ? vivo.leituras.get(chave(device, medida)) : undefined;
    return leitura && Date.now() - Date.parse(leitura.timestamp) < 1800000 ? leitura.valor : null;
  }, [vivo, vivo.versao]); // eslint-disable-line react-hooks/exhaustive-deps

  // alarmes reais (painel do setor e agente)
  useEffect(() => {
    if (!empresa) return;
    const carregar = () => api<{ grupos: GrupoAlarmeSetor[] }>(`/v1/alarmes?empresa=${empresa}&horas=24`).then((r) => setAlarmes(r.grupos))
      .catch(() => undefined);
    void carregar();
    const relogio = window.setInterval(carregar, 30000);
    return () => window.clearInterval(relogio);
  }, [empresa]);

  const pontoDe = useCallback((x: number, z: number) => new THREE.Vector3(x, rede && relevo ? alturaCena(relevo, rede, x, z) : 0, z), [rede, relevo]);
  const selecionar = useCallback((e: Equipamento) => {
    setSelecionado(e);
    setAlvo({ ponto: pontoDe(e.x, e.z), distancia: e.tipo === "reservatorio" ? 110 : ["bomba", "poco_profundo", "elevatoria"].includes(e.tipo) ? 22 : 30 });
  }, [pontoDe]);
  const escolherSetor = (setor: string) => {
    if (setorSelecionado === setor) { setSetorSelecionado(null); return; }
    setSetorSelecionado(setor);
    if (!rede) return;
    const nos = [...(nosSetor.get(setor) ?? [])].map((id) => rede.nos.get(id)).filter(Boolean) as { x: number; z: number }[];
    if (!nos.length) return;
    const xs = nos.map((n) => n.x), zs = nos.map((n) => n.z);
    const tamanho = Math.max(Math.max(...xs) - Math.min(...xs), Math.max(...zs) - Math.min(...zs), 200);
    setAlvo({ ponto: pontoDe((Math.min(...xs) + Math.max(...xs)) / 2, (Math.min(...zs) + Math.max(...zs)) / 2), distancia: tamanho * 1.1 });
  };
  useEffect(() => {
    const foco = parametros.get("foco");
    if (foco && rede && relevo) { const e = rede.equipamentos.find((q) => q.id === foco); if (e) selecionar(e); }
  }, [rede, relevo]); // eslint-disable-line react-hooks/exhaustive-deps

  const situacao = useMemo(() => (rede && setorSelecionado && !origemEpanet
    ? situacaoSetorAoVivo(rede, setorSelecionado, nosSetor.get(setorSelecionado) ?? new Set(), ler, vivo.velhos, alarmes) : null),
  [rede, nosSetor, setorSelecionado, ler, vivo.velhos, alarmes, origemEpanet]);

  /** Câmera na porta da casa, olhando o quadro de comando (posição do quadro no modelo da estação, em escala de perto). */
  const entrarNaCasa = (e: Equipamento) => {
    setDentroDe(e);
    const base = pontoDe(e.x, e.z);
    setAlvo({ ponto: base.clone().add(new THREE.Vector3(-2.3, 1.5, -1.9)), distancia: 3.2, direcao: new THREE.Vector3(0.35, 0.25, 1) });
  };


  const valorAoVivo = (e: Equipamento): string | null => {
    if (!e.deviceId) return null;
    if (e.tipo === "reservatorio") {
      const pct = ler(e.deviceId, "nivel_reservatorio_pct"), m = ler(e.deviceId, "nivel_m");
      if (pct === null) return m === null ? null : `${m.toFixed(2)} m de água`;  // US-2306: Costa Rica só mede a altura
      return `Nível ${pct.toFixed(0)} %${m !== null ? ` · ${m.toFixed(2)} m de água` : ""}`;
    }
    if (e.tipo === "ponto_pressao") { const p = ler(e.deviceId, "pressao_rede_mca"); return p === null ? null : `Pressão ${p.toFixed(1)} mca`; }
    const recalque = ler(e.deviceId, "pressao_recalque_mca");
    if (e.tipo === "poco_profundo" && recalque !== null && ler(e.deviceId, "bomba_ligada") === null && ler(e.deviceId, "potencia_ativa_kw") === null)
      return `Recalque ${recalque.toFixed(1)} mca`;  // US-2306: poço de Costa Rica mede só a pressão de recalque
    if (["bomba", "poco_profundo", "elevatoria"].includes(e.tipo)) {
      const ligada = ler(e.deviceId, "bomba_ligada") ?? ((ler(e.deviceId, "potencia_ativa_kw") ?? 0) > 0.5 ? 1 : 0);
      const corrente = ler(e.deviceId, "corrente_fase_a");
      return `Bomba ${ligada >= 0.5 ? "ligada" : "desligada"}${corrente !== null ? ` · ${corrente.toFixed(1)} A` : ""}`;
    }
    if (e.tipo === "vrp") { const j = ler(e.deviceId, "pressao_jusante_mca"); return j === null ? null : `Jusante ${j.toFixed(1)} mca`; }
    const q = ler(e.deviceId, "vazao_instantanea_m3h");
    return q === null ? null : `Vazão ${q.toLocaleString("pt-BR", { maximumFractionDigits: 1 })} m³/h`;
  };

  const extensao = rede?.extensao ?? 3000;
  const valorSel = selecionado ? valorAoVivo(selecionado) : null;
  return (
    <section className="palco-3d" aria-label="SCADA 3D">
      {rede && relevo ? (
        <Canvas camera={{ position: [extensao * 0.15, extensao * 0.55, extensao * 0.75], fov: 45, near: 0.5, far: 50000 }}>
          <color attach="background" args={["#0a0f13"]} />
          <fog attach="fog" args={["#0a0f13", extensao * 0.9, extensao * 2.4]} />
          <hemisphereLight args={["#c6d8e6", "#1a2328", 1.2]} />
          <directionalLight position={[2000, 3000, 1000]} color="#fff4e6" intensity={2.4} />
          <Suspense fallback={null}>
            <Cena rede={rede} relevo={relevo} setores={nomesSetores} destaquePiloto={destaque} rotulos={rotulos}
              setorSelecionado={setorSelecionado} modo="setor" sim={sim} horaInteira={Math.floor(tempo.current.hora)} tempo={tempo} tubulacao={tubulacao}
              ler={ler} empresa={empresa} quadros={quadros && !dentroDe} focoId={selecionado?.id} aoSelecionar={selecionar} aoFocar={(p) => setAlvo({ ponto: p, distancia: 90 })} />
            {origemEpanet && <PontosGemeo gemeo={gemeo} rede={rede} relevo={relevo} />}
            {mostrarEdificacoes && edificacoes && <Edificacoes3d lista={edificacoes.features} rede={rede} relevo={relevo} aoSelecionar={setEdificacao} />}
          </Suspense>
          <OrbitControls makeDefault zoomToCursor zoomSpeed={2.2} enableDamping dampingFactor={0.12} screenSpacePanning={false}
            maxPolarAngle={Math.PI * 0.48} minDistance={1.5} maxDistance={extensao * 3} onStart={() => setAlvo(null)} />
          <Foco alvo={alvo} aoChegar={() => setAlvo(null)} />
          <Relogio tempo={tempo} />
          <Medidor />
        </Canvas>
      ) : null}
      <p className="estado-3d" id="estado">{erro ?? estado}{cenarioSimulado
        ? " · DEMONSTRAÇÃO: posições, trechos e parâmetros fictícios"
        : vivo.conectado ? " · dados ao vivo" : " · reconectando a telemetria…"}</p>
      {erro && <div className="painel aviso"><strong>SCADA 3D indisponível</strong><p>{erro}</p></div>}
      {(rede || origens.length > 0) && (
        <aside className={`painel legenda${legendaAberta ? "" : " recolhida"}`} aria-label="Setores de abastecimento">
          <div className="topo-legenda">
            <strong>SETORES ({setores.length})</strong>
            <button type="button" className="recolher" aria-expanded={legendaAberta} onClick={() => setLegendaAberta(!legendaAberta)}
              title={legendaAberta ? "recolher" : "abrir"}>{legendaAberta ? "–" : "+"}</button>
          </div>
          {legendaAberta && <>
          <div className="opcoes-legenda">
            <label>Origem <select aria-label="Origem da rede" value={origem} onChange={(e) => setOrigem(e.target.value)}>
              <option value="">Desenho vigente (DXF)</option>{origens.map((i) => <option key={i.id} value={i.id}>EPANET · {nomesMunicipios[i.municipio!] ?? i.municipio} · {i.rede} · {i.arquivo}</option>)}
            </select></label>
            <label><input type="checkbox" checked={quadros} onChange={(e) => setQuadros(e.target.checked)} /> quadros</label>
            <label title="Tubos acompanham o relevo; espessura ampliada para leitura. Desmarque para ver apenas as linhas.">
              <input type="checkbox" checked={tubulacao} onChange={(e) => setTubulacao(e.target.checked)} /> tubulação 3D</label>
            <label><input type="checkbox" checked={rotulos} onChange={(e) => setRotulos(e.target.checked)} /> rótulos</label>
            {edificacoes && edificacoes.features.length > 0 && <label title={edificacoes.atribuicao.join(" · ")}>
              <input type="checkbox" checked={mostrarEdificacoes} onChange={(e) => setMostrarEdificacoes(e.target.checked)} /> edificações</label>}
            <label><input type="checkbox" checked={destaque} onChange={(e) => setDestaque(e.target.checked)} /> piloto RSE</label>
            {origemEpanet && <label><input type="checkbox" checked={gemeo.ligado} onChange={(e) => gemeo.setLigado(e.target.checked)} /> gêmeo digital</label>}
          </div>
          <input className="filtro-setor" type="search" placeholder="filtrar setor…" value={filtroSetor} aria-label="Filtrar setores"
            onChange={(e) => setFiltroSetor(e.target.value)} />
          <span className="suave">{cenarioSimulado ? "leituras fictícias de demonstração"
            : origemEpanet ? `setores do modelo · ${rede?.equipamentos.filter((e) => e.deviceId).length ?? 0} equipamentos com dispositivo vinculado` : "clique para ver o setor · dados ao vivo"}</span>
          <ul className="lista-setores">
            {setores.filter((s) => s.setor.toLowerCase().includes(filtroSetor.trim().toLowerCase())).map((s) => (
              <li key={s.setor}>
                <button type="button" className={setorSelecionado === s.setor ? "ativo" : ""} onClick={() => escolherSetor(s.setor)}>
                  <span className="cor" style={{ background: corDoSetor(s.setor, nomesSetores) }} />{s.setor}<span className="suave"> {s.trechos}</span>
                </button>
              </li>
            ))}
          </ul>
          </>}
        </aside>
      )}
      {situacao && <PainelSetor situacao={situacao} empresa={empresa} aoFechar={() => setSetorSelecionado(null)} />}
      {origemEpanet && <PainelGemeo key={origem} gemeo={gemeo} />}
      {dentroDe && rede && (
        <aside className="painel detalhe quadro-comando" aria-label={`Quadro de comando de ${dentroDe.id}`}>
          <strong>QUADRO DE COMANDO · {dentroDe.id}</strong>
          <span className="suave">dentro da casa · dados ao vivo do quadro</span>
          <BlocosQuadro empresa={empresa} ler={ler} membros={rede.equipamentos.filter((m) => ["bomba", "poco_profundo", "elevatoria"].includes(m.tipo)
            && Math.hypot(m.x - dentroDe.x, m.z - dentroDe.z) < 15)} />
          <button type="button" onClick={() => setDentroDe(null)}>sair da casa</button>
        </aside>
      )}
      {edificacao && (
        <aside className="painel detalhe" aria-label="Edificação" aria-live="polite">
          <strong>{edificacao.properties.nome || `Edificação ${NOME_USO[edificacao.properties.uso] ?? edificacao.properties.uso}`}</strong>
          <p>{edificacao.properties.altura_m ?? "—"} m · {edificacao.properties.pavimentos ?? "—"} pavimento(s){edificacao.properties.altura_estimada ? " (estimado)" : ""}
            {edificacao.properties.area_m2 ? ` · ${Math.round(edificacao.properties.area_m2)} m²` : ""}</p>
          <p className="suave">setor {edificacao.properties.setor ?? "—"} (aproximado) · nó {edificacao.properties.no_epanet ?? "—"} · fonte {edificacao.properties.fonte}</p>
          <button type="button" onClick={() => setEdificacao(null)}>fechar</button>
        </aside>
      )}
      {selecionado && !situacao && !dentroDe && (
        <aside className="painel detalhe" aria-live="polite">
          <strong>{selecionado.id || "(sem identificação)"}</strong>
          <p>{NOMES[selecionado.tipo] ?? selecionado.tipo}{selecionado.detalhe ? ` · ${selecionado.detalhe}` : ""}</p>
          <p className="suave">setor {selecionado.setor} · {selecionado.situacao ?? ""} · {selecionado.lat.toFixed(6)}, {selecionado.lon.toFixed(6)}</p>
          {valorSel ? <p className="ao-vivo">{valorSel} <span className="selo-ia">{selecionado.deviceId && vivo.velhos.has(selecionado.deviceId) ? "última leitura · atrasada" : "ao vivo"}</span></p>
            : <p className="suave">{selecionado.deviceId ? "Sem leitura recente no servidor" : "sem telemetria ligada"}</p>}
          <div className="acoes">
            {["bomba", "poco_profundo", "elevatoria"].includes(selecionado.tipo) &&
              <button type="button" onClick={() => entrarNaCasa(selecionado)}>Entrar na casa (quadro)</button>}
            <button type="button" onClick={() => escolherSetor(selecionado.setor)}>Ver setor {selecionado.setor}</button>
            {selecionado.deviceId && <Link to={`/cco/equipamento/${selecionado.deviceId}`}>Abrir no CCO ›</Link>}
            {selecionado.ativoId && <Link to={`/cadastro/${selecionado.ativoId}`}>Ficha técnica ›</Link>}
            <Link to={`/rede?foco=${encodeURIComponent(selecionado.id)}`}>Simulação na rede ›</Link>
          </div>
          <button type="button" onClick={() => { setSelecionado(null); setDentroDe(null); }}>fechar</button>
        </aside>
      )}
      {mostrarEdificacoes && edificacoes && edificacoes.features.length > 0 &&
        <p className="atribuicao-3d suave" aria-label="Fontes das edificações">Edificações: {edificacoes.atribuicao.join(" · ")}</p>}
      <p className="dica suave">Role para aproximar · duplo clique voa até o ponto · clique num setor ou equipamento · manobra só no CCO</p>
    </section>
  );
}
